// ✅ New static SettingsPage with layout matching Employee Details
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import './App.css';

const API_BASE = 'https://qbo.omnivistaweb.com';

interface CompanySettings {
  company_logo: string;
  company_name: string;
  company_salary_memo_1?: string;
}

interface QboConfig {
  qbo_client_id: string;
  qbo_client_secret: string;
  qbo_redirect_uri: string;
  qbo_employer_id: string;
  qbo_authorization_code: string;
  qbo_realm_id: string;
  qbo_random_salary_data: boolean;
}

interface SifFooterConfig {
  sif_record_type: string;
  sif_employer_id: string;
  sif_routing_code: string;
  sif_payment_currency: string;
  sif_employer_reference: string;
}

interface EmailConfig {
  email_from: string;
  email_smtp_host: string;
  email_smtp_port: number;
  email_auth_user: string;
  email_auth_pass: string;
  email_salary_in_text: boolean;
}


const SettingsPage = () => {
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    company_logo: '',
    company_name: '',
    company_salary_memo_1:'',
  });
  
  const [qboConfig, setQboConfig] = useState<QboConfig>({
    qbo_client_id: '',
    qbo_client_secret: '',
    qbo_redirect_uri: '',
    qbo_employer_id: '',
    qbo_authorization_code: '',
    qbo_realm_id: '',
    qbo_random_salary_data: false
  });

  const [sifFooterConfig, setSifFooterConfig] = useState<SifFooterConfig>({
    sif_record_type: '',
    sif_employer_id: '',
    sif_routing_code: '',
    sif_payment_currency: '',
    sif_employer_reference: ''
  });

  const [emailConfig, setEmailConfig] = useState<EmailConfig>({
    email_from: '',
    email_smtp_host: '',
    email_smtp_port: 587,
    email_auth_user: '',
    email_auth_pass: '',
    email_salary_in_text: true
  });
  

  const [expandedSection, setExpandedSection] = useState<string[]>(['company', 'qbo', 'sif', 'email']);

  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE}/api/settings/company`).then(res => {
        console.log('🏢 Company config loaded:', res.data);
        setCompanySettings(res.data);
      }),      
      axios.get(`${API_BASE}/api/settings/qbo`).then(res => {
        console.log('✅ QBO data loaded:', res.data);
        setQboConfig(res.data);
      }),
      axios.get(`${API_BASE}/api/settings/sif-footer`).then(res => {
        console.log('✅ SIF Footer data loaded:', res.data);
        setSifFooterConfig(res.data);
      }),
      axios.get(`${API_BASE}/api/settings/email`).then(res => {
        console.log('✅ Email config loaded:', res.data);
        setEmailConfig(res.data);
      })      
    ]).then(() => {
      setConfigLoaded(true);
    });
  }, []);

  const toggleSection = (key: string) => {
    setExpandedSection(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('logo', file);

    try {
      const res = await axios.post(`${API_BASE}/api/settings/company/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      console.log('📸 Logo upload response:', res.data);
      if (res.data?.path) {
        setCompanySettings(prev => ({ ...prev, company_logo: res.data.path }));
        alert('✅ Logo uploaded!');
      } else {
        alert('⚠️ Upload succeeded, but no path returned!');
      }
      
    } catch (err) {
      console.error('❌ Error uploading logo:', err);
      alert('❌ Failed to upload logo');
    }
  };

  const handleSaveQbo = async () => {
    try {
      const payload = { ...qboConfig };
      await axios.post(`${API_BASE}/api/settings/qbo`, payload);
      alert('✅ QBO config saved.');
    } catch (err) {
      console.error('❌ Error saving QBO config:', err);
      alert('❌ Error saving QBO config');
    }
  };

  const handleSaveSif = async () => {
    try {
      const payload = { ...sifFooterConfig };
      await axios.post(`${API_BASE}/api/settings/sif-footer`, payload);
      alert('✅ SIF config saved.');
    } catch (err) {
      console.error('❌ Error saving SIF config:', err);
      alert('❌ Error saving SIF config');
    }
  };

  const handleSaveEmail = async () => {
    try {
      const payload = { ...emailConfig };
      await axios.post(`${API_BASE}/api/settings/email`, payload);
      alert('✅ Email config saved.');
    } catch (err) {
      console.error('❌ Error saving Email config:', err);
      alert('❌ Error saving Email config');
    }
  };

  return (
    <div className="dashboard-wrapper" style={{ padding: 20 }}>
      {configLoaded && (
        <>
          {/* Company Config Section */}
          <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => toggleSection('company')}>☰</span>
              <h4 style={{ margin: 0, fontWeight: 600, color: '#1e1b4b' }}>Company Configuration</h4>
            </div>
            {expandedSection.includes('company') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <TextBox label="Company Name" value={companySettings.company_name || ''} onValueChanged={e => setCompanySettings({ ...companySettings, company_name: e.value })} width={300} />
                <TextBox
                  label="Salary Memo (Footer Text)"
                  value={companySettings.company_salary_memo_1 || ''}
                  onValueChanged={e => setCompanySettings({ ...companySettings, company_salary_memo_1: e.value })}
                  width={600}
                  height={100}
                  stylingMode="outlined"
                  mode="text"
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <label>Upload Logo (PNG/JPG)</label>
                  <input type="file" accept="image/*" onChange={handleUploadLogo} />
                  {companySettings.company_logo && (
                    <img
                    src={`${API_BASE}${companySettings.company_logo}`}
                    alt="Company Logo"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    style={{ marginTop: 10, maxHeight: 60, borderRadius: 4 }}
                  />
                  )}
                </div>

                <Button text="Save" onClick={async () => {
                  try {
                    await axios.post(`${API_BASE}/api/settings/company`, companySettings);
                    alert('✅ Company config saved.');
                  } catch (err) {
                    console.error('❌ Error saving Company config:', err);
                    alert('❌ Error saving Company config');
                  }
                }} style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }} />
              </div>
            )}
          </div>
          {/* QBO Section */}
          <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => toggleSection('qbo')}>☰</span>
              <h4 style={{ margin: 0, fontWeight: 600, color: '#1e1b4b' }}>QBO Configuration</h4>
            </div>
            {expandedSection.includes('qbo') && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                <TextBox label="Client ID" value={qboConfig.qbo_client_id || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_client_id: e.value })} width={300} />
                <TextBox label="Client Secret" value={qboConfig.qbo_client_secret || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_client_secret: e.value })} width={300} />
                <TextBox label="Redirect URI" value={qboConfig.qbo_redirect_uri || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_redirect_uri: e.value })} width={300} />
                <TextBox label="Employer ID" value={qboConfig.qbo_employer_id || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_employer_id: e.value })} width={200} />
                <TextBox label="Authorization Code" value={qboConfig.qbo_authorization_code || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_authorization_code: e.value })} width={300} />
                <TextBox label="Realm ID" value={qboConfig.qbo_realm_id || ''} onValueChanged={e => setQboConfig({ ...qboConfig, qbo_realm_id: e.value })} width={200} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label>Random Salary Data</label>
                  <input type="checkbox" checked={qboConfig.qbo_random_salary_data} onChange={(e) => setQboConfig({ ...qboConfig, qbo_random_salary_data: e.target.checked })} />
                </div>
                <Button text="Save" onClick={handleSaveQbo} style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }} />
              </div>
            )}
          </div>

          {/* SIF Footer Section */}
          <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => toggleSection('sif')}>☰</span>
              <h4 style={{ margin: 0, fontWeight: 600, color: '#1e1b4b' }}>SIF Footer Configuration</h4>
            </div>
            {expandedSection.includes('sif') && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                <TextBox label="Record Type" value={sifFooterConfig.sif_record_type || ''} onValueChanged={e => setSifFooterConfig({ ...sifFooterConfig, sif_record_type: e.value })} width={180} />
                <TextBox label="Employer ID" value={sifFooterConfig.sif_employer_id || ''} onValueChanged={e => setSifFooterConfig({ ...sifFooterConfig, sif_employer_id: e.value })} width={200} />
                <TextBox label="Routing Code" value={sifFooterConfig.sif_routing_code || ''} onValueChanged={e => setSifFooterConfig({ ...sifFooterConfig, sif_routing_code: e.value })} width={200} />
                <TextBox label="Payment Currency" value={sifFooterConfig.sif_payment_currency || ''} onValueChanged={e => setSifFooterConfig({ ...sifFooterConfig, sif_payment_currency: e.value })} width={200} />
                <TextBox label="Employer Reference" value={sifFooterConfig.sif_employer_reference || ''} onValueChanged={e => setSifFooterConfig({ ...sifFooterConfig, sif_employer_reference: e.value })} width={300} />
                <Button text="Save" onClick={handleSaveSif} style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }} />
              </div>
            )}
          </div>

          {/* Email Config Section */}
          <div style={{ background: '#fff', padding: 16, borderRadius: 8, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => toggleSection('email')}>☰</span>
              <h4 style={{ margin: 0, fontWeight: 600, color: '#1e1b4b' }}>Email Configuration</h4>
            </div>
            {expandedSection.includes('email') && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 }}>
                <TextBox label="From" value={emailConfig.email_from} onValueChanged={e => setEmailConfig({ ...emailConfig, email_from: e.value })} width={300} />
                <TextBox label="SMTP Host" value={emailConfig.email_smtp_host} onValueChanged={e => setEmailConfig({ ...emailConfig, email_smtp_host: e.value })} width={300} />
                <TextBox label="SMTP Port" value={String(emailConfig.email_smtp_port)} onValueChanged={e => setEmailConfig({ ...emailConfig, email_smtp_port: parseInt(e.value || '0') })} width={150} />
                <TextBox label="Auth User" value={emailConfig.email_auth_user} onValueChanged={e => setEmailConfig({ ...emailConfig, email_auth_user: e.value })} width={300} />
                <TextBox label="Auth Password" value={emailConfig.email_auth_pass} onValueChanged={e => setEmailConfig({ ...emailConfig, email_auth_pass: e.value })} mode="password" width={300} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <label>Email Salary in Text</label>
                  <input type="checkbox" checked={emailConfig.email_salary_in_text} onChange={e => setEmailConfig({ ...emailConfig, email_salary_in_text: e.target.checked })} />
                </div>
                <Button text="Save" onClick={handleSaveEmail} style={{ backgroundColor: 'var(--blue-primary)', color: 'white' }} />
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
};

export default SettingsPage;
